/**
 * Dependency-free <head> manager (works with React 19, no react-helmet).
 * Every tag we own carries `data-seo` so it can be replaced on each route change.
 * Tags that are unique per document (description, canonical, og:title...) are
 * upserted by selector so the static defaults in public/index.html are reused
 * instead of duplicated.
 */
import { SITE_URL, SITE_NAME, LOCALES, OG_IMAGE_PATH, absoluteUrl } from '../seo/seoConfig.mjs';

const head = () => (typeof document !== 'undefined' ? document.head : null);

const upsert = (selector, create, attrs) => {
  const h = head();
  if (!h) return null;
  let el = h.querySelector(selector);
  if (!el) {
    el = create();
    h.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => {
    if (v === null || v === undefined || v === '') el.removeAttribute(k);
    else el.setAttribute(k, v);
  });
  el.setAttribute('data-seo', '1');
  return el;
};

const setMetaName = (name, content) => upsert(
  `meta[name="${name}"]`,
  () => Object.assign(document.createElement('meta'), { name }),
  { content },
);

const setMetaProperty = (property, content) => upsert(
  `meta[property="${property}"]`,
  () => { const m = document.createElement('meta'); m.setAttribute('property', property); return m; },
  { content },
);

const removeAll = (selector) => {
  const h = head();
  if (!h) return;
  h.querySelectorAll(selector).forEach(el => el.remove());
};

/**
 * Apply a full SEO state for the current route.
 * @param {object} o
 * @param {string} o.title            Full <title>
 * @param {string} [o.description]
 * @param {string} [o.keywords]
 * @param {string} [o.robots]         e.g. 'index, follow' | 'noindex, nofollow'
 * @param {string} [o.path]           Relative path used for canonical + og:url (omit for private pages)
 * @param {string} [o.lang]           'fr' | 'en' -> <html lang>, og:locale
 * @param {object} [o.alternates]     { fr: '/fr', en: '/en', 'x-default': '/fr' }
 * @param {string} [o.ogType]         'website' | 'article'
 * @param {string} [o.ogImage]        Absolute or relative image URL
 * @param {Array|object} [o.jsonLd]   One or more schema.org objects
 */
export const applySeo = ({
  title,
  description,
  keywords,
  robots = 'index, follow',
  path,
  lang,
  alternates,
  ogType = 'website',
  ogImage,
  jsonLd,
}) => {
  if (typeof document === 'undefined') return;
  if (title) document.title = title;
  if (lang) document.documentElement.setAttribute('lang', lang);

  setMetaName('description', description || '');
  setMetaName('keywords', keywords || '');
  setMetaName('robots', robots);
  setMetaName('googlebot', robots);

  const canonical = path ? absoluteUrl(path) : null;
  if (canonical) {
    upsert('link[rel="canonical"]', () => Object.assign(document.createElement('link'), { rel: 'canonical' }), { href: canonical });
  } else {
    removeAll('link[rel="canonical"]');
  }

  removeAll('link[rel="alternate"][hreflang]');
  if (alternates) {
    Object.entries(alternates).forEach(([hreflang, altPath]) => {
      const link = document.createElement('link');
      link.rel = 'alternate';
      link.setAttribute('hreflang', hreflang);
      link.href = absoluteUrl(altPath);
      link.setAttribute('data-seo', '1');
      document.head.appendChild(link);
    });
  }

  const image = ogImage ? (ogImage.startsWith('http') ? ogImage : absoluteUrl(ogImage)) : absoluteUrl(OG_IMAGE_PATH);
  setMetaProperty('og:site_name', SITE_NAME);
  setMetaProperty('og:type', ogType);
  setMetaProperty('og:title', title || SITE_NAME);
  setMetaProperty('og:description', description || '');
  setMetaProperty('og:url', canonical || SITE_URL);
  setMetaProperty('og:image', image);
  setMetaProperty('og:image:width', '1200');
  setMetaProperty('og:image:height', '630');
  setMetaProperty('og:locale', LOCALES[lang] || LOCALES.fr);
  removeAll('meta[property="og:locale:alternate"]');
  if (lang && alternates) {
    Object.keys(LOCALES).filter(l => l !== lang && alternates[l]).forEach(l => {
      const m = document.createElement('meta');
      m.setAttribute('property', 'og:locale:alternate');
      m.setAttribute('content', LOCALES[l]);
      m.setAttribute('data-seo', '1');
      document.head.appendChild(m);
    });
  }

  setMetaName('twitter:card', 'summary_large_image');
  setMetaName('twitter:title', title || SITE_NAME);
  setMetaName('twitter:description', description || '');
  setMetaName('twitter:image', image);

  removeAll('script[type="application/ld+json"][data-seo]');
  if (jsonLd) {
    const items = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
    items.filter(Boolean).forEach(obj => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-seo', '1');
      script.text = JSON.stringify(obj);
      document.head.appendChild(script);
    });
  }
};
